import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { Loader2, Eye, EyeOff, Users } from 'lucide-react';
import { applyPageMetadata, applyStructuredData } from '../utils/seo';
import axios from 'axios';

export default function RegisterBartendersPage() {
  const [step, setStep] = useState(1);
  const [createdUser, setCreatedUser] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    user_type: 'bartender',
    phone: '',
    city: '',
    state: '',
    experience_years: '',
    skills: '',
    bio: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const cleanupMeta = applyPageMetadata({
      title: 'Create Your Bartender Profile | BarsConnect',
      description:
        'Create your free bartender profile on BarsConnect and get discovered by bars hiring flexible shifts and event gigs.',
      keywords:
        'create bartender profile, bartender signup, bartender jobs registration, barsconnect bartender',
      canonicalUrl: 'https://barsconnect.com/register/bartenders',
      robots: 'index,follow',
    });

    const cleanupSchema = applyStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://barsconnect.com/' },
          { '@type': 'ListItem', position: 2, name: 'Register Bartenders', item: 'https://barsconnect.com/register/bartenders' },
        ],
      },
    ]);

    return () => {
      cleanupMeta();
      cleanupSchema();
    };
  }, []);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateStep1 = () => {
    if (!formData.first_name.trim() || !formData.last_name.trim()) return 'Name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!formData.password) return 'Password is required';
    if (formData.password.length < 6) return 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateStep1();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const { confirmPassword, ...submitData } = formData;
      const result = await register({
        ...submitData,
        user_type: 'bartender',
      });

      if (!result.success) {
        setError(result.error || 'Failed to create account');
        return;
      }

      setCreatedUser(result.user);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const skillList = formData.skills
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      await axios.put('/bartenders/profile', {
        age: 0,
        experience_years: Number(formData.experience_years || 0),
        skills: skillList,
        bio: formData.bio,
        location: {
          city: formData.city,
          state: formData.state,
        },
        certifications: [],
        availability: {},
        available_tonight: false,
      });

      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to complete profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" data-testid="register-bartenders-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary font-['Barlow_Condensed'] uppercase tracking-tight">BARSCONNECT</h1>
          <p className="text-muted-foreground mt-2">Create your bartender profile and start getting discovered.</p>
        </div>

        <Card className="bg-white/5 backdrop-blur-md border border-white/5 shadow-2xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center font-['Barlow_Condensed'] uppercase">
              {step === 1 ? 'Step 1: Create Account' : 'Step 2: Complete Profile'}
            </CardTitle>
            <CardDescription className="text-center">
              {step === 1 ? 'Name, email, and password only.' : 'Add profile details to get hired faster.'}
            </CardDescription>
          </CardHeader>

          {step === 1 ? (
            <form onSubmit={handleStep1Submit}>
              <CardContent className="space-y-4">
                {error && <div className="p-3 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}
                <div className="flex items-center gap-2 text-sm text-primary">
                  <Users className="h-4 w-4" /> Bartender role preselected
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input id="first_name" value={formData.first_name} onChange={(e) => handleChange('first_name', e.target.value)} data-testid="register-bartender-first-name" />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input id="last_name" value={formData.last_name} onChange={(e) => handleChange('last_name', e.target.value)} data-testid="register-bartender-last-name" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} data-testid="register-bartender-email" />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => handleChange('password', e.target.value)} data-testid="register-bartender-password" />
                    <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3" onClick={() => setShowPassword((prev) => !prev)}>
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Input id="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={(e) => handleChange('confirmPassword', e.target.value)} data-testid="register-bartender-confirm-password" />
                    <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3" onClick={() => setShowConfirmPassword((prev) => !prev)}>
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-3">
                <Button type="submit" className="w-full" disabled={loading} data-testid="register-bartender-step1-submit">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : 'Create Bartender Account'}
                </Button>
                <p className="text-center text-sm text-muted-foreground">
                  Already have an account? <Link to="/login" className="text-primary hover:underline">Bartender Login</Link>
                </p>
              </CardFooter>
            </form>
          ) : (
            <form onSubmit={handleStep2Submit}>
              <CardContent className="space-y-4">
                {error && <div className="p-3 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}
                <p className="text-xs text-muted-foreground">Welcome {createdUser?.first_name || 'Bartender'} — finish your profile to improve discovery.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input id="city" value={formData.city} onChange={(e) => handleChange('city', e.target.value)} data-testid="register-bartender-city" />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Input id="state" value={formData.state} onChange={(e) => handleChange('state', e.target.value)} data-testid="register-bartender-state" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="experience_years">Years Experience</Label>
                  <Input id="experience_years" type="number" min="0" value={formData.experience_years} onChange={(e) => handleChange('experience_years', e.target.value)} data-testid="register-bartender-experience" />
                </div>
                <div>
                  <Label htmlFor="skills">Skills (comma-separated)</Label>
                  <Input id="skills" value={formData.skills} onChange={(e) => handleChange('skills', e.target.value)} data-testid="register-bartender-skills" />
                </div>
                <div>
                  <Label htmlFor="bio">Short Bio</Label>
                  <Input id="bio" value={formData.bio} onChange={(e) => handleChange('bio', e.target.value)} data-testid="register-bartender-bio" />
                </div>
              </CardContent>
              <CardFooter>
                <Button type="submit" className="w-full" disabled={loading} data-testid="register-bartender-step2-submit">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Complete Profile'}
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
